import {vnTemplate} from "../../../vendor/valnet/valnet_jslib/vnKnockoutJsTemplates/vnTemplate";
import {DragAndDropHandler} from "../../utils/DragAndDropHandler";
import {filterTag_template} from "./filterTag_template";
import {filterArticle_template} from "./filterArticle_template";

declare var ko: any;

/** Shared with `config_marketplace.filterSimpleOnChange` / tag search `idElement`. */
export const MP_HOME_FILTER_FEATURED = "tagFilterFeatured";
export const MP_HOME_FILTER_NEW = "tagFilterNew";
export const MP_HOME_FILTER_BARGAIN = "tagFilterBargain";

type MpHomeTagSectionDef = {
    filterId: string;
    chipsHostId: string;
    vmKey: string;
    settingsKey: string;
    max: number;
    title: string;
    helpText: string;
};

/** Configurable Marketplace homepage tag-picker subsections (first four Home Page Listing blocks). */
const MP_HOME_TAG_SECTIONS: MpHomeTagSectionDef[] = [
    {
        filterId: MP_HOME_FILTER_FEATURED,
        chipsHostId: "mpFeatOfWeekTagChips",
        vmKey: "marketplace_feat_of_week",
        settingsKey: "marketplace-feat-of-week",
        max: 3,
        title: "Featured Vehicles of the Week",
        helpText: "Select vehicle tags (up to 3) for the featured carousel on /marketplace/.",
    },
    {
        filterId: MP_HOME_FILTER_NEW,
        chipsHostId: "mpNewOfWeekTagChips",
        vmKey: "marketplace_new_of_week",
        settingsKey: "marketplace-new-of-week",
        max: 5,
        title: "New Listings of the Week",
        helpText: "Select up to 5 vehicle tags to show first; the section displays up to 15 listings total, filled with recent nearby listings.",
    },
    {
        filterId: MP_HOME_FILTER_BARGAIN,
        chipsHostId: "mpBargainOfWeekTagChips",
        vmKey: "marketplace_bargain_of_week",
        settingsKey: "marketplace-bargain-of-week",
        max: 3,
        title: "Bargains of the Week",
        helpText: "Select vehicle tags (up to 3) to show first in the bargains carousel on /marketplace/.",
    },
];

const MP_HOME_TAG_SECTION_BY_FILTER: { [filterId: string]: MpHomeTagSectionDef } = {};
for (let i = 0; i < MP_HOME_TAG_SECTIONS.length; i++) {
    MP_HOME_TAG_SECTION_BY_FILTER[MP_HOME_TAG_SECTIONS[i].filterId] = MP_HOME_TAG_SECTIONS[i];
}

const MP_BEST_CATEGORY_EMPTY_LABEL = "No vehicle tag selected";
const MP_BEST_CATEGORY_COUNT = 8;
const MP_BEST_CATEGORY_KEY = "marketplace-best-by-category";

const MP_BUYING_GUIDES_EMPTY_LABEL = "No article selected";
const MP_BUYING_GUIDES_COUNT = 4;
const MP_BUYING_GUIDES_KEY = "marketplace-buying-guides";

type MarketplaceTagWeekRow = { id: any; name: string; formattedTagTypeAndDataset?: string };

function normalizeTagWeekRows(raw: any[], max: number): MarketplaceTagWeekRow[] {
    const out: MarketplaceTagWeekRow[] = [];
    if (!Array.isArray(raw) || raw.length === 0 || max <= 0) {
        return out;
    }
    const seen: { [key: string]: boolean } = {};
    for (let i = 0; i < raw.length; i++) {
        const entry = raw[i];
        if (!entry || entry.id == null) {
            continue;
        }
        const key = String(entry.id);
        if (seen[key]) {
            continue;
        }
        seen[key] = true;
        const row: MarketplaceTagWeekRow = {
            id: entry.id,
            name: entry.name != null ? String(entry.name) : ""
        };
        if (entry.formattedTagTypeAndDataset != null && String(entry.formattedTagTypeAndDataset) !== "") {
            row.formattedTagTypeAndDataset = String(entry.formattedTagTypeAndDataset);
        }
        out.push(row);
        if (out.length >= max) {
            break;
        }
    }
    return out;
}

const PROVIDER_EXTRA_CONDITIONS: Array<{ providerId: string, extraConditionFields: Array<{ label: string, value: string }> }> = [
    {
        providerId: 'autoweb',
        extraConditionFields: [
            { label: 'Program ID', value: 'props.programId' },
        ],
    },
];

export class config_marketplace_template extends vnTemplate {

    protected homeTagFilters: { [filterId: string]: filterTag_template };
    protected buyingGuideFilters: { [filterId: string]: filterArticle_template };
    protected brandSiteUrl: string = "";
    protected bestCategoryRows: any[] = [];

    protected initPartial(): void {
        this.homeTagFilters = {};
        this.buyingGuideFilters = {};
        const vm = this.getTemplateViewModel();
        for (let i = 0; i < MP_HOME_TAG_SECTIONS.length; i++) {
            const section = MP_HOME_TAG_SECTIONS[i];
            this.homeTagFilters[section.filterId] = this.createHomeTagFilter(section.filterId, vm, section.chipsHostId);
        }
        for (let i = 0; i < MP_BEST_CATEGORY_COUNT; i++) {
            this.homeTagFilters["tagFilterBestCategory_" + i] = this.createHomeTagFilter("tagFilterBestCategory_" + i, vm);
        }
        for (let i = 0; i < MP_BUYING_GUIDES_COUNT; i++) {
            const filterId = "articleFilterBuyingGuide_" + i;
            vm["marketplace_buying_guide_slot_" + i] = ko.observable(null);
            const filter = new filterArticle_template(filterId, vm, this.caller);
            filter.setShowDetails(true);
            filter.setIsLoadingAnimationEnable(true);
            filter.setSelectionMode(filterArticle_template.SINGLE_SELECTION_MODE);
            this.buyingGuideFilters[filterId] = filter;
        }
    }

    private createHomeTagFilter(filterId: string, vm: any, chipsHostId?: string): filterTag_template {
        const filter = new filterTag_template(filterId, vm, this.caller);
        if (chipsHostId) {
            filter.setTagContainerId(chipsHostId);
            filter.asMultipleSelectionMode();
        }
        filter.setPlaceholderText("Search vehicle tag…");
        filter.setShowType(false);
        return filter;
    }

    public getTagFilterByElementId(id: string): filterTag_template | null {
        return this.homeTagFilters[id] || null;
    }

    public setHomeTagSuggestions(filterId: string, tags: any[]): void {
        const filter = this.homeTagFilters[filterId];
        if (!filter) {
            return;
        }
        filter.setTags(tags);
        filter.setIsShowTagSuggestions(tags.length > 0);
    }

    public syncHomeTagSectionFromFilter(filterId: string, notifyOnClear: boolean = false): void {
        const section = MP_HOME_TAG_SECTION_BY_FILTER[filterId];
        const filter = this.homeTagFilters[filterId];
        if (section && filter) {
            this.getTemplateViewModel()[section.vmKey](
                normalizeTagWeekRows(filter.getTagObjs(), section.max)
            );
            return;
        }
        const bestIndex = filterId.indexOf("tagFilterBestCategory_");
        if (bestIndex === 0 && filter) {
            const i = parseInt(filterId.replace("tagFilterBestCategory_", ""), 10);
            const tags = normalizeTagWeekRows(filter.getTagObjs(), 1);
            this.getTemplateViewModel()["marketplace_best_slot_" + i](tags);
            const tagLabel = this.builtTemplate.querySelector("#mp-best-label-" + i);
            if (tagLabel) {
                tagLabel.textContent = tags.length ? tags[0].name : MP_BEST_CATEGORY_EMPTY_LABEL;
            }
            filter.clearSearchInputOnly();
            return;
        }
        const buyingGuideIndex = filterId.indexOf("articleFilterBuyingGuide_");
        if (buyingGuideIndex === 0) {
            this.syncBuyingGuideSlotFromFilter(filterId, notifyOnClear);
        }
    }

    private syncBuyingGuideSlotFromFilter(filterId: string, notifyOnClear: boolean): void {
        const filter = this.buyingGuideFilters[filterId];
        if (!filter) {
            return;
        }
        const i = parseInt(filterId.replace("articleFilterBuyingGuide_", ""), 10);
        const vm = this.getTemplateViewModel();
        const slotKey = "marketplace_buying_guide_slot_" + i;
        const previousArticle = vm[slotKey] ? vm[slotKey]() : null;
        const slotArticle = this.articleRowFromSlot(filter.getArticleObj());
        if (notifyOnClear) {
            this.notifyBuyingGuideSlotCleared(previousArticle, slotArticle);
        }
        vm[slotKey](slotArticle);
        this.updateBuyingGuideSlotUi(i, slotArticle);
    }

    private articleRowFromSlot(article: any): { id: any; title: string; permalink?: string } | null {
        if (!article || article.id == null) {
            return null;
        }
        const row: { id: any; title: string; permalink?: string } = {
            id: article.id,
            title: article.title != null ? String(article.title) : ""
        };
        if (article.permalink != null && String(article.permalink) !== "") {
            row.permalink = String(article.permalink);
        }
        return row;
    }

    private notifyBuyingGuideSlotCleared(previousArticle: any, slotArticle: any): void {
        if (!previousArticle || previousArticle.id == null || slotArticle) {
            return;
        }
        window.alert("This Buying Guide slot is now empty. After saving, it will auto-fill with a top Buyer's Advice article from the last 30 days and can refresh weekly.");
    }

    private getArticleLiveUrl(permalink: string): string {
        const siteUrl = this.getBrandSiteUrl();
        if (!siteUrl || !permalink) {
            return "";
        }
        const path = permalink.charAt(0) === "/" ? permalink : "/" + permalink;
        return siteUrl + path;
    }

    private getBrandId(): number {
        return this.caller.getVnAppZone().getApp().params.idBrand;
    }

    private getBrandSiteUrl(): string {
        if (this.brandSiteUrl) {
            return this.brandSiteUrl.replace(/\/$/, "");
        }
        const domain = window.sessionStorage.getItem("currentBrandDomain") || "";
        return domain.replace(/\/$/, "");
    }

    private updateBuyingGuideSlotUi(
        i: number,
        slotArticle: { id: any; title: string; permalink?: string } | null
    ): void {
        const titleLabel = this.builtTemplate.querySelector("#mp-buying-guide-label-" + i);
        const linksEl = this.builtTemplate.querySelector("#mp-buying-guide-links-" + i);
        const liveLink = this.builtTemplate.querySelector("#mp-buying-guide-live-" + i);
        const emakiLink = this.builtTemplate.querySelector("#mp-buying-guide-emaki-" + i);

        if (titleLabel) {
            titleLabel.textContent = slotArticle ? slotArticle.title : MP_BUYING_GUIDES_EMPTY_LABEL;
        }

        if (!linksEl || !liveLink || !emakiLink) {
            return;
        }

        if (!slotArticle || slotArticle.id == null) {
            linksEl.setAttribute("hidden", "hidden");
            return;
        }

        linksEl.removeAttribute("hidden");
        emakiLink.setAttribute(
            "href",
            window.location.origin + "/brand/" + this.getBrandId() + "/articles/view/" + slotArticle.id
        );
        emakiLink.removeAttribute("hidden");

        const liveUrl = this.getArticleLiveUrl(slotArticle.permalink || "");
        if (liveUrl) {
            liveLink.setAttribute("href", liveUrl);
            liveLink.removeAttribute("hidden");
        } else {
            liveLink.setAttribute("hidden", "hidden");
        }
    }

    public syncAllHomeTagSectionsFromFilters(): void {
        for (let i = 0; i < MP_HOME_TAG_SECTIONS.length; i++) {
            this.syncHomeTagSectionFromFilter(MP_HOME_TAG_SECTIONS[i].filterId);
        }
        for (let i = 0; i < MP_BEST_CATEGORY_COUNT; i++) {
            this.syncHomeTagSectionFromFilter("tagFilterBestCategory_" + i);
        }
        for (let i = 0; i < MP_BUYING_GUIDES_COUNT; i++) {
            this.syncHomeTagSectionFromFilter("articleFilterBuyingGuide_" + i);
        }
    }

    private renderBestCategoryRowsHtml(): string {
        let html = "";
        for (let i = 0; i < MP_BEST_CATEGORY_COUNT; i++) {
            const filter = this.homeTagFilters["tagFilterBestCategory_" + i];
            html += `<div class="mp-best-category-row">
                <div class="mp-tag-slot mp-best-category-tag-input">${filter.getHtmlBuild()}</div>
                <span class="mp-best-category-slot-name" id="mp-best-slot-name-${i}"></span>
                <span class="mp-best-category-label" id="mp-best-label-${i}">${MP_BEST_CATEGORY_EMPTY_LABEL}</span>
            </div>`;
        }
        return html;
    }

    private initBestCategoryUi(settings: any): void {
        const vm = this.getTemplateViewModel();
        const raw = settings && settings[MP_BEST_CATEGORY_KEY];
        const rows = Array.isArray(raw) ? raw : [];
        this.bestCategoryRows = rows;

        for (let i = 0; i < MP_BEST_CATEGORY_COUNT; i++) {
            const row = rows[i];
            const slotName = this.builtTemplate.querySelector("#mp-best-slot-name-" + i);
            const tagLabel = this.builtTemplate.querySelector("#mp-best-label-" + i);
            const shortLabel = row && row.short_label ? row.short_label : "";
            if (slotName) {
                slotName.textContent = shortLabel ? shortLabel + ": " : "";
            }
            const tagRows = row && row.tag ? [{ id: row.tag.id, name: row.tag.name }] : [];
            if (tagLabel) {
                tagLabel.textContent = row && row.tag ? row.tag.name : MP_BEST_CATEGORY_EMPTY_LABEL;
            }
            vm["marketplace_best_slot_" + i](tagRows);
            const filter = this.homeTagFilters["tagFilterBestCategory_" + i];
            if (filter) {
                filter.setSelectedTagsFromRequestParams(tagRows);
            }
        }

        vm.bestCategoryReady(true);
    }

    private renderBuyingGuidesRowsHtml(): string {
        let html = "";
        for (let i = 0; i < MP_BUYING_GUIDES_COUNT; i++) {
            const filter = this.buyingGuideFilters["articleFilterBuyingGuide_" + i];
            html += `<div class="mp-buying-guide-row">
                <span class="mp-buying-guide-position">${i + 1}</span>
                <div class="mp-article-slot mp-buying-guide-article-input">${filter.getHtmlBuild()}</div>
                <span class="mp-buying-guide-label" id="mp-buying-guide-label-${i}" hidden="hidden">${MP_BUYING_GUIDES_EMPTY_LABEL}</span>
                <div class="mp-buying-guide-links" id="mp-buying-guide-links-${i}">
                    <a class="mp-buying-guide-link" target="_blank" rel="noopener noreferrer" id="mp-buying-guide-live-${i}">Carbuzz 🏎️</a>
                    <a class="mp-buying-guide-link" target="_blank" rel="noopener noreferrer" id="mp-buying-guide-emaki-${i}">Emaki ⚙️</a>
                </div>
            </div>`;
        }
        return html;
    }

    private initBuyingGuidesUi(settings: any): void {
        const vm = this.getTemplateViewModel();
        const articles = settings && settings[MP_BUYING_GUIDES_KEY] && Array.isArray(settings[MP_BUYING_GUIDES_KEY])
            ? settings[MP_BUYING_GUIDES_KEY]
            : [];

        for (let i = 0; i < MP_BUYING_GUIDES_COUNT; i++) {
            const row = this.articleRowFromSlot(articles[i]);
            const filter = this.buyingGuideFilters["articleFilterBuyingGuide_" + i];
            vm["marketplace_buying_guide_slot_" + i](row);
            this.updateBuyingGuideSlotUi(i, row);
            if (filter) {
                filter.setSelectedArticle(row || undefined);
            }
        }

        vm.buyingGuideReady(true);
    }

    public setArticleFilterAutocomplete(idElement: string, articles: any[]): void {
        const filter = this.buyingGuideFilters[idElement];
        if (filter) {
            filter.setOptions(articles);
        }
    }

    private renderMpHomeTagSectionHtml(section: MpHomeTagSectionDef): string {
        const filter = this.homeTagFilters[section.filterId];
        return `
                       <div class="form-group mp-home-subsection">
                           <div class="form-body w-pinned">
                               <div class="pinned-form entry">
                                    <div class="w-title">
                                        <span class="pinned-title">${section.title}</span>
                                    </div>
                               </div>
                           </div>
                           <div class="form-group">
                               <label class="form-label">${section.helpText}</label>
                           </div>
                           <div id="${section.chipsHostId}" class="mp-feat-tag-chips-host"></div>
                           <div class="mp-tag-slot">${filter.getHtmlBuild()}</div>
                       </div>`;
    }

    protected getMainElementType(): string {
        return "div";
    }

    protected getTemplateName(): string {
        return "config_marketplace_template";
    }

    protected getTemplateHtml(): string {
        return /*html*/`
            <div class="marketplace-page">
               <div class="list-header w-justify marketplace-page-tabs">
                   <div class="w-left">
                       <div class="w-tabs">
                           <div class="radio-to-tab">
                               <input type="radio" id="mp_view_settings" value="settings" data-bind="checked: viewType"/>
                               <label class="radio-label" for="mp_view_settings" data-bind="text: internalMode() ? 'Internal Marketplace Settings' : 'Marketplace Settings'"></label>
                           </div>
                           <div class="radio-to-tab">
                               <input type="radio" id="mp_view_providers" value="providers" data-bind="checked: viewType"/>
                               <label class="radio-label" for="mp_view_providers" data-bind="text: internalMode() ? 'Tiers' : 'Providers'"></label>
                           </div>
                       </div>
                   </div>
               </div>

            <div class="w-form marketplace-config" data-bind="visible: viewType() === 'settings'">
               <div class="form-header">
                  <input type="hidden">
                  <h3 class="form-title" data-bind="text: internalMode() ? 'Internal Marketplace Settings' : 'Marketplace Settings'"></h3>
               </div>

               <div class="form-body">

                   <h3 class="form-sub-title icon i-listing-2-preview" data-bind="text: internalMode() ? 'Tier Waterfall' : 'Provider Sponsor (Waterfall Order)'"></h3>
                   <div class="form-group">
                       <label class="form-label" data-bind="text: internalMode() ? 'Configure the tier waterfall. Drag to reorder. Each tier can appear only once.' : 'Configure the provider waterfall. Drag to reorder. Each provider can appear only once.'"></label>
                   </div>
                   <div class="mp-warning">
                       This order controls which <span data-bind="text: internalMode() ? 'tier’s' : 'provider’s'"></span> listings are shown first in sponsored placements. It does <strong>not</strong> affect how VIN duplicates are resolved across <span data-bind="text: internalMode() ? 'tiers' : 'providers'"></span> — to configure that, use the <strong data-bind="text: internalMode() ? 'Tier VIN Duplicate Priority' : 'Provider VIN Duplicate Priority'"></strong> section below.
                   </div>
                   <div class="form-group" data-bind="foreach: provider_sponsor, dragAndDrop: { observableArray: provider_sponsor, childrenSelector: '.mp-entry', dragHandleSelector: '.mp-drag' }">
                       <div class="mp-entry">
                           <div class="mp-drag icon i-drag">
                               <span class="mp-position" data-bind="text: ($index() + 1)"></span>
                           </div>
                           <div class="mp-provider" data-bind="text: $parent.displayProvider(provider)"></div>
                           <div class="mp-field">
                               <label>Places</label>
                               <input type="number" min="1" data-bind="value: places">
                           </div>
                           <button class="btn border only-icon icon border i-delete" type="button" data-bind="event: { click: $parent.onRemoveFromSponsor }"><span class="hidden-text">Remove</span></button>
                       </div>
                   </div>
                   <div class="form-group" data-bind="visible: provider_sponsor().length === 0">
                       <label class="form-label" style="text-align: center; font-style: italic;">No entries in the sponsor list.</label>
                   </div>
                   <div class="mp-error" data-bind="visible: totalPlacesExceeded">
                       Total sponsor places cannot exceed 10 (current: <span data-bind="text: totalPlaces"></span>).
                   </div>

                   <!-- Add entry form -->
                   <div class="form-group" data-bind="visible: addable_providers().length > 0">
                       <div class="mp-entry mp-add-form">
                           <div class="mp-field">
                               <label data-bind="text: internalMode() ? 'Tier' : 'Provider'"></label>
                               <select data-bind="options: addable_providers, optionsText: displayProvider, value: newEntryProvider, optionsCaption: '-- Select --'"></select>
                           </div>
                           <div class="mp-field">
                               <label>Places</label>
                               <input type="number" min="1" data-bind="value: newEntryPlaces">
                           </div>
                           <button class="btn border icon i-plus" type="button" data-bind="event: { click: onAddToSponsor }, enable: newEntryProvider()"><span>Add</span></button>
                       </div>
                   </div>

                   <h3 class="form-sub-title icon i-listing-2-preview" data-bind="text: internalMode() ? 'DB Force per Tier' : 'DB Force'"></h3>
                   <div class="form-group">
                       <label class="form-label" data-bind="text: internalMode() ? 'Force one tier for the DB for-sale page. One tier only, places cannot exceed 12.' : 'Force a single provider for DB for-sale page. One provider only, places cannot exceed 12.'"></label>
                   </div>
                   <div class="form-group" data-bind="visible: db_force_provider()">
                       <div class="mp-entry">
                           <div class="mp-provider" data-bind="text: displayProvider(db_force_provider())"></div>
                           <div class="mp-field">
                               <label>Places</label>
                               <input type="number" min="1" max="12" data-bind="value: db_force_places">
                           </div>
                           <div class="mp-field" data-bind="visible: db_force_extraConditionFields().length > 0">
                               <label>Condition Field</label>
                               <select data-bind="options: db_force_extraConditionFields, optionsText: 'label', optionsValue: 'value', value: db_force_extraConditionField, optionsCaption: '-- None --'"></select>
                           </div>
                           <div class="mp-field" data-bind="visible: db_force_extraConditionField()">
                               <label>Condition Value</label>
                               <input type="text" data-bind="value: db_force_extraConditionValue">
                           </div>
                           <button class="btn border only-icon icon border i-delete" type="button" data-bind="event: { click: onClearDbForce }"><span class="hidden-text">Clear</span></button>
                       </div>
                   </div>
                   <div class="mp-error" data-bind="visible: db_force_placesExceeded">
                       DB Force places cannot exceed 12 (current: <span data-bind="text: db_force_places"></span>).
                   </div>
                   <div class="form-group" data-bind="visible: !db_force_provider() && db_force_addable_providers().length > 0">
                       <div class="mp-entry mp-add-form">
                           <div class="mp-field">
                               <label data-bind="text: internalMode() ? 'Tier' : 'Provider'"></label>
                               <select data-bind="options: db_force_addable_providers, optionsText: displayProvider, value: db_force_newProvider, optionsCaption: '-- Select --'"></select>
                           </div>
                           <button class="btn border icon i-plus" type="button" data-bind="event: { click: onAddDbForce }, enable: db_force_newProvider()"><span>Add</span></button>
                       </div>
                   </div>

                   <h3 class="form-sub-title icon i-listing-2-preview" data-bind="text: internalMode() ? 'Tier VIN Duplicate Priority' : 'Provider VIN Duplicate Priority'"></h3>
                   <div class="form-group">
                       <label class="form-label" data-bind="text: internalMode() ? 'Configure the priority order for resolving VIN duplicates across tiers. Drag to reorder.' : 'Configure the priority order for resolving VIN duplicates across providers. Drag to reorder.'"></label>
                   </div>
                   <div class="form-group" data-bind="foreach: vin_duplicate_priority, dragAndDrop: { observableArray: vin_duplicate_priority, childrenSelector: '.mp-entry', dragHandleSelector: '.mp-drag' }">
                       <div class="mp-entry">
                           <div class="mp-drag icon i-drag">
                               <span class="mp-position" data-bind="text: ($index() + 1)"></span>
                           </div>
                           <div class="mp-provider" data-bind="text: $parent.displayProvider($data)"></div>
                       </div>
                   </div>
                   <div class="form-group" data-bind="visible: vin_duplicate_priority().length === 0">
                       <label class="form-label" style="text-align: center; font-style: italic;">No providers available.</label>
                   </div>

                   <div data-bind="visible: !internalMode() && available_providers().length > 0">
                       <h3 class="form-sub-title icon i-listing-2-preview">Available Providers</h3>
                       <div class="form-group">
                           <label class="form-label">These providers are not blocked and can be added to the sponsor list or blocked.</label>
                       </div>
                       <div class="form-group" data-bind="foreach: available_providers">
                           <div class="form-body w-pinned">
                               <div class="pinned-form entry">
                                    <div class="w-title">
                                        <span class="pinned-title" data-bind="text: $data"></span>
                                    </div>
                                    <div class="w-btn">
                                        <button class="btn border only-icon icon border highlight i-delete" type="button" data-bind="event: { click: $parent.onBlockProvider }"><span class="hidden-text">Block</span></button>
                                    </div>
                               </div>
                           </div>
                       </div>
                   </div>

                   <div data-bind="visible: !internalMode() && blocked_providers().length > 0">
                       <h3 class="form-sub-title icon i-listing-2-preview">Blocked Providers</h3>
                       <div class="form-group">
                           <label class="form-label">These providers are blocked and will not be used.</label>
                       </div>
                       <div class="form-group" data-bind="foreach: blocked_providers">
                           <div class="form-body w-pinned">
                               <div class="pinned-form entry">
                                    <div class="w-title">
                                        <span class="pinned-title" data-bind="text: $data"></span>
                                    </div>
                                    <div class="w-btn">
                                        <button class="btn border icon i-plus" type="button" data-bind="event: { click: $parent.onUnblockProvider }"><span class="hidden-text">Unblock</span></button>
                                    </div>
                               </div>
                           </div>
                       </div>
                   </div>

                   <!-- ko if: internalMode -->
                   <div class="form-group" data-bind="visible: availableInternalDealers().length > 0">
                       <h3 class="form-sub-title icon i-listing-2-preview">Available Dealers</h3>
                       <label class="form-label">These dealers are available for internal marketplace search.</label>
                       <div class="form-body w-pinned" data-bind="foreach: availableInternalDealers">
                           <div class="pinned-form entry">
                               <div class="w-title">
                                   <span class="pinned-title" data-bind="text: name"></span>
                                   <small data-bind="text: $parent.describeInternalDealer($data)"></small>
                               </div>
                               <div class="w-btn">
                                   <button class="btn border only-icon icon border highlight i-delete" type="button" data-bind="event: { click: $parent.onBlockInternalDealer }"><span class="hidden-text">Block</span></button>
                               </div>
                           </div>
                       </div>
                   </div>
                   <div class="form-group" data-bind="visible: blockedInternalDealers().length > 0">
                       <h3 class="form-sub-title icon i-listing-2-preview">Blocked Dealers</h3>
                       <label class="form-label">These dealers are excluded from internal marketplace search.</label>
                       <div class="form-body w-pinned" data-bind="foreach: blockedInternalDealers">
                           <div class="pinned-form entry">
                               <div class="w-title">
                                   <span class="pinned-title" data-bind="text: name"></span>
                                   <small data-bind="text: $parent.describeInternalDealer($data)"></small>
                               </div>
                               <div class="w-btn">
                                   <button class="btn border icon i-plus" type="button" data-bind="event: { click: $parent.onUnblockInternalDealer }"><span class="hidden-text">Unblock</span></button>
                               </div>
                           </div>
                       </div>
                   </div>
                   <!-- /ko -->

                   <div data-bind="visible: !internalMode()">
                       <h3 class="form-sub-title icon i-listing-2-preview">Home Page Listing</h3>
                       <div class="form-group">
                           <label class="form-label">Curated tag-driven sections on the Marketplace home page (/marketplace/).</label>
                       </div>
                       ${MP_HOME_TAG_SECTIONS.map(section => this.renderMpHomeTagSectionHtml(section)).join("")}
                       <div class="form-group mp-home-subsection">
                           <div class="form-body w-pinned">
                               <div class="pinned-form entry">
                                    <div class="w-title">
                                        <span class="pinned-title" data-bind="css: { 'mp-error-title': bestCategoryReady() && bestCategoryHasEmptyField() }">Best Cars by Category</span>
                                        
                                    </div>
                                    <div class="mp-error" data-bind="visible: bestCategoryReady() && bestCategoryHasEmptyField()">
                                        Please make sure all Best Cars by Category fields are filled before saving.
                                    </div>
                               </div>
                           </div>
                           <div class="form-group">
                               <label class="form-label">One vehicle tag per category.</label>
                           </div>
                           <p class="form-label" data-bind="visible: !bestCategoryReady()">Loading categories…</p>
                           <div class="mp-best-category-rows" data-bind="visible: bestCategoryReady">${this.renderBestCategoryRowsHtml()}</div>
                       </div>
                       <div class="form-group mp-home-subsection">
                           <div class="form-body w-pinned">
                               <div class="pinned-form entry">
                                    <div class="w-title">
                                        <span class="pinned-title">Buying Guides</span>
                                    </div>
                               </div>
                           </div>
                           <div class="form-group">
                               <label class="form-label">Select up to 4 articles. Slot 1 is the hero card. Empty slots auto-fill with top Buyer's Advice articles from the last 30 days.</label>
                           </div>
                           <div class="mp-warning mp-buying-guide-warning">
                               Selected articles stay pinned. Empty slots refresh weekly, preferring current-year articles first.
                           </div>
                           <p class="form-label" data-bind="visible: !buyingGuideReady()">Loading buying guides…</p>
                           <div class="mp-buying-guide-rows" data-bind="visible: buyingGuideReady">${this.renderBuyingGuidesRowsHtml()}</div>
                       </div>
                   </div>

               </div>

               <div class="form-footer">
                  <button data-bind="event: { click: onClickCancelMarketplaceSettings }" title="There will be no changes made" class="btn" type="button">Cancel</button>
                  <button data-bind="event: { click: onClickSaveMarketplaceSettings }, disable: !canSaveMarketplaceSettings()" class="btn highlight" type="button">Save</button>
               </div>
            </div>

            <div data-bind="visible: viewType() === 'providers', template: { name: 'config_marketplace_providers_template', data: providersPanel }"></div>
            </div>

            <style>
                .marketplace-page-tabs {
                    margin-bottom: 1rem;
                }
                .marketplace-page .w-form.marketplace-config,
                .marketplace-page .w-form.providers-config {
                    width: calc(100% - 4rem);
                    display: block;
                    box-sizing: border-box;
                }
                .marketplace-config .mp-entry {
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    gap: 1.5rem;
                    padding: 0.4rem 0.6rem;
                    margin-bottom: 2px;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    background-color: #f9f9f9;
                }
                .marketplace-config .mp-drag {
                    display: flex;
                    align-items: center;
                    gap: 0.2rem;
                    cursor: grab;
                    flex-shrink: 0;
                }
                .marketplace-config .mp-drag::before {
                    font-size: 1.2rem;
                    width: auto;
                }
                .marketplace-config .mp-position {
                    font-weight: 700;
                    font-size: 1.1rem;
                    min-width: 1.2rem;
                    text-align: center;
                }
                .marketplace-config .mp-provider {
                    font-weight: 700;
                    font-size: 1.2rem;
                    min-width: 6rem;
                }
                .marketplace-config .mp-field {
                    display: flex;
                    align-items: center;
                    gap: 0.3rem;
                    flex-shrink: 0;
                }
                .marketplace-config .mp-field label {
                    font-size: 0.85em;
                    opacity: 0.7;
                    margin: 0;
                    white-space: nowrap;
                }
                .marketplace-config .mp-field input[type="number"],
                .marketplace-config .mp-field input[type="text"] {
                    width: 65px;
                    padding: 0.25rem 0.4rem;
                    text-align: center;
                    border: 1px solid #ccc;
                    border-radius: 3px;
                }
                .marketplace-config .mp-field select {
                    min-width: 120px;
                    padding: 0.25rem 0.4rem;
                    border: 1px solid #ccc;
                    border-radius: 3px;
                }
                .marketplace-config .mp-entry > .btn {
                    margin-left: auto;
                    flex-shrink: 0;
                }
                .marketplace-config .mp-add-form {
                    border-style: dashed;
                    background-color: transparent;
                }
                .marketplace-config .mp-error {
                    color: #c0392b;
                    font-size: 0.9em;
                    padding: 0.3rem 0;
                }
                .marketplace-config .mp-error-title {
                    color: #c0392b;
                }
                .marketplace-config .mp-warning {
                    background-color: #fffbe6;
                    border: 1px solid #ffe58f;
                    border-radius: 4px;
                    color: #7d5a00;
                    font-size: 0.95em;
                    padding: 0.5rem 1.25rem;
                    margin: 2rem;
                    font-size: 11px;
                }
                .marketplace-config .mp-home-subsection {
                    margin-bottom: 1rem;
                }
                .marketplace-config .mp-buying-guide-warning {
                    margin: 0 0 1rem 0;
                }
                .marketplace-config .mp-tag-slot {
                    max-width: 28rem;
                }
                .marketplace-config .mp-feat-tag-chips-host {
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    gap: 0.5rem 1rem;
                    margin: 0.5rem 0;
                }
                .marketplace-config .mp-buying-guide-rows,
                .marketplace-config .mp-best-category-rows {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 0.65rem 2rem;
                    margin-top: 0.5rem;
                }
                .marketplace-config .mp-buying-guide-row {
                    display: flex;
                    flex-direction: row;
                    flex-wrap: nowrap;
                    align-items: center;
                    gap: 0.35rem;
                    min-width: 0;
                    width: 100%;
                }
                .marketplace-config .mp-best-category-row {
                    display: flex;
                    flex-direction: row;
                    flex-wrap: nowrap;
                    align-items: center;
                    gap: 0.35rem;
                    min-width: 0;
                }
                .marketplace-config .mp-best-category-slot-name {
                    font-weight: 700;
                    font-size: 1.3rem;
                    white-space: nowrap;
                    flex-shrink: 0;
                }
                .marketplace-config .mp-buying-guide-position {
                    font-weight: 700;
                    font-size: 1.3rem;
                    white-space: nowrap;
                    flex-shrink: 0;
                    min-width: 1.2rem;
                    text-align: center;
                }
                .marketplace-config .mp-best-category-label {
                    font-weight: 600;
                    white-space: nowrap;
                    flex: 0 0 auto;
                }
                .marketplace-config .mp-buying-guide-label {
                    display: none;
                }
                .marketplace-config .mp-buying-guide-links {
                    flex: 0 0 auto;
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 0.15rem;
                }
                .marketplace-config .mp-buying-guide-link {
                    font-size: 0.85em;
                    text-decoration: underline;
                    margin-bottom: 1.5rem;
                }
                .marketplace-config .mp-buying-guide-article-input {
                    flex: 0 0 75%;
                    margin-right: 1rem;
                    width: 75%;
                    max-width: 75%;
                }
                .marketplace-config .mp-best-category-tag-input {
                    flex: 0 0 200px;
                    margin-right: 1rem;
                    width: 200px;
                }
                .marketplace-config .mp-buying-guide-article-input .w-spec-search,
                .marketplace-config .mp-best-category-tag-input .w-spec-search {
                    width: 100%;
                }
            </style>
        `;
    }

    protected buildViewModel(): any {
        const vm: any = {
            internalMode: ko.observable(false),
            bestCategoryReady: ko.observable(false),
            buyingGuideReady: ko.observable(false),
            viewType: ko.observable('settings'),
            providersPanel: null,
            providers: ko.observableArray([]),              // Master list of provider names
            internalDealers: ko.observableArray([]),
            availableInternalDealers: null,
            blockedInternalDealers: null,
            displayProvider: (provider: any) => {
                const value = String(ko.unwrap(provider) || '');
                return vm.internalMode() && ['silver', 'gold', 'titanium'].indexOf(value) !== -1
                    ? value.charAt(0).toUpperCase() + value.slice(1)
                    : value;
            },
            describeInternalDealer: (dealer: any) => {
                return dealer.dms + ':' + dealer.sourceDealerId + ' · ' + vm.displayProvider(dealer.tier);
            },
            provider_sponsor: ko.observableArray([]),       // Waterfall entries: {provider, places (observable)}
            blocked_providers: ko.observableArray([]),      // Blocked provider names
            vin_duplicate_priority: ko.observableArray([]), // Reordered providers for VIN duplicate resolution
            available_providers: null,                      // Computed: providers not blocked
            addable_providers: null,                        // Computed: available providers not already in sponsor list

            newEntryProvider: ko.observable(null),
            newEntryPlaces: ko.observable(''),

            db_force_provider: ko.observable(null as string | null),
            db_force_places: ko.observable(1),
            db_force_extraConditionField: ko.observable(null as string | null),
            db_force_extraConditionValue: ko.observable(''),
            db_force_newProvider: ko.observable(null as string | null),

            onRemoveFromSponsor: (entry) => {
                const sponsor = this.getTemplateViewModel().provider_sponsor;
                sponsor.remove(entry);
            },

            onAddToSponsor: () => {
                const vm = this.getTemplateViewModel();
                const provider = vm.newEntryProvider();
                if (!provider) return;

                const rawPlaces = vm.newEntryPlaces();
                const parsedPlaces = parseInt(rawPlaces, 10);
                const places = isNaN(parsedPlaces) ? null : parsedPlaces;

                vm.provider_sponsor.push({
                    provider: provider,
                    places: ko.observable(places)
                });

                // Reset form
                vm.newEntryProvider(null);
                vm.newEntryPlaces('');
            },

            onBlockProvider: (provider) => {
                alert("Your changes will apply to /marketplace/ only. Orphan pages will not be affected.");

                const vm = this.getTemplateViewModel();

                // Remove entry for this provider from the sponsor list
                const toRemove = vm.provider_sponsor().filter(e => e.provider === provider);
                toRemove.forEach(e => vm.provider_sponsor.remove(e));

                // Remove from db_force if this provider is forced
                if (vm.db_force_provider() === provider) {
                    vm.db_force_provider(null);
                    vm.db_force_places(1);
                    vm.db_force_extraConditionField(null);
                    vm.db_force_extraConditionValue('');
                    vm.db_force_newProvider(null);
                }

                // Add to blocked list
                vm.blocked_providers.push(provider);
            },

            onUnblockProvider: (provider) => {
                const vm = this.getTemplateViewModel();
                vm.blocked_providers.remove(provider);
            },

            onBlockInternalDealer: (dealer) => {
                dealer.isBlocked(true);
            },

            onUnblockInternalDealer: (dealer) => {
                dealer.isBlocked(false);
            },

            onAddDbForce: () => {
                const vm = this.getTemplateViewModel();
                const provider = vm.db_force_newProvider();
                if (!provider) return;
                vm.db_force_provider(provider);
                vm.db_force_places(1);
                vm.db_force_extraConditionField(null);
                vm.db_force_extraConditionValue('');
                const entry = PROVIDER_EXTRA_CONDITIONS.filter(e => e.providerId === provider)[0];
                if (entry && entry.extraConditionFields.length === 1) {
                    vm.db_force_extraConditionField(entry.extraConditionFields[0].value);
                }
                vm.db_force_newProvider(null);
            },

            onClearDbForce: () => {
                const vm = this.getTemplateViewModel();
                vm.db_force_provider(null);
                vm.db_force_places(1);
                vm.db_force_extraConditionField(null);
                vm.db_force_extraConditionValue('');
                vm.db_force_newProvider(null);
            },

            onClickCancelMarketplaceSettings: () => {
                this.caller.cancelSiteSettings();
            },

            onClickSaveMarketplaceSettings: () => {
                this.caller.saveMarketplaceSettings();
            }
        };

        for (let i = 0; i < MP_HOME_TAG_SECTIONS.length; i++) {
            vm[MP_HOME_TAG_SECTIONS[i].vmKey] = ko.observableArray([]);
        }

        for (let i = 0; i < MP_BEST_CATEGORY_COUNT; i++) {
            vm["marketplace_best_slot_" + i] = ko.observableArray([]);
        }

        vm.bestCategoryHasEmptyField = ko.computed(() => {
            for (let i = 0; i < MP_BEST_CATEGORY_COUNT; i++) {
                const slot = vm["marketplace_best_slot_" + i]();
                if (!slot.length || !slot[0] || !slot[0].id) {
                    return true;
                }
            }

            return false;
        });

        // Computed: providers not in blocked list (available for blocking)
        vm.available_providers = ko.computed(() => {
            const all = vm.providers();
            const blocked = vm.blocked_providers();
            return all.filter(p => blocked.indexOf(p) === -1);
        });

        vm.availableInternalDealers = ko.computed(() => {
            return vm.internalDealers().filter(dealer => !dealer.isBlocked());
        });

        vm.blockedInternalDealers = ko.computed(() => {
            return vm.internalDealers().filter(dealer => dealer.isBlocked());
        });

        // Computed: available providers not already in sponsor list (one slot per provider)
        vm.addable_providers = ko.computed(() => {
            const available = vm.available_providers();
            const sponsorNames = vm.provider_sponsor().map(e => e.provider);
            return available.filter(p => sponsorNames.indexOf(p) === -1);
        });

        // Computed: sum of all sponsor places
        vm.totalPlaces = ko.computed(() => {
            return vm.provider_sponsor().reduce((sum, entry) => {
                const val = parseInt(ko.unwrap(entry.places), 10);
                return sum + (isNaN(val) ? 0 : val);
            }, 0);
        });

        vm.totalPlacesExceeded = ko.computed(() => {
            return vm.totalPlaces() > 10;
        });

        // DB Force: one provider, max 12 places
        vm.db_force_placesExceeded = ko.computed(() => {
            if (!vm.db_force_provider()) return false;
            const val = parseInt(ko.unwrap(vm.db_force_places), 10);
            return !isNaN(val) && val > 12;
        });

        vm.canSaveMarketplaceSettings = ko.computed(() => {
            return !vm.totalPlacesExceeded()
                && !vm.db_force_placesExceeded()
                && (vm.internalMode() || !vm.bestCategoryHasEmptyField());
        });

        vm.db_force_addable_providers = ko.computed(() => {
            return vm.available_providers();
        });

        // Computed: extra condition fields for the currently selected db_force provider
        vm.db_force_extraConditionFields = ko.computed(() => {
            const provider = vm.db_force_provider();
            if (!provider) return [];
            const entry = PROVIDER_EXTRA_CONDITIONS.filter(e => e.providerId === provider)[0];
            return entry ? entry.extraConditionFields : [];
        });

        return vm;
    }

    public setMarketplaceSettings(settings: any, brandSiteUrl?: string) {
        if (brandSiteUrl) {
            this.brandSiteUrl = String(brandSiteUrl);
        }
        const defaultProviders = ['lotlinx', 'carzing', 'carsAndBids'];

        // Set master providers list
        const providers = (settings && settings.providers && Array.isArray(settings.providers))
            ? settings.providers
            : defaultProviders;
        this.getTemplateViewModel().providers(providers);
        const internalDealers = settings && Array.isArray(settings.internal_dealers)
            ? settings.internal_dealers.map((dealer: any) => ({
                dms: dealer.dms,
                sourceDealerId: dealer.sourceDealerId,
                name: dealer.name,
                tier: dealer.tier,
                isBlocked: ko.observable(!!dealer.isBlocked)
            }))
            : [];
        this.getTemplateViewModel().internalDealers(internalDealers);

        // Set blocked list
        const blocked = (settings && settings.blocked_providers && Array.isArray(settings.blocked_providers))
            ? settings.blocked_providers
            : [];
        this.getTemplateViewModel().blocked_providers(blocked);

        // Set sponsor list — convert to observable entry objects (one per provider)
        let sponsor = [];
        const sponsorSettings = settings && Array.isArray(settings.tier_sponsor)
            ? settings.tier_sponsor
            : settings && settings.provider_sponsor;
        if (Array.isArray(sponsorSettings)) {
            sponsor = sponsorSettings.map(entry => {
                if (typeof entry === 'string') {
                    return {
                        provider: entry,
                        places: ko.observable(null)
                    };
                }
                return {
                    provider: entry.provider,
                    places: ko.observable(entry.places != null ? entry.places : null)
                };
            });
        } else if (settings && settings.providers_priority && Array.isArray(settings.providers_priority)) {
            // Migrate old providers_priority format — deduplicate by taking first occurrence
            const seen = new Set();
            sponsor = settings.providers_priority
                .map(entry => {
                    const provider = typeof entry === 'string' ? entry : entry.provider;
                    if (seen.has(provider)) return null;
                    seen.add(provider);
                    return {
                        provider: provider,
                        places: ko.observable(typeof entry === 'object' && entry.places != null ? entry.places : null)
                    };
                })
                .filter(e => e !== null);
        } else if (settings && settings.providers && Array.isArray(settings.providers)) {
            sponsor = settings.providers
                .filter(p => blocked.indexOf(p) === -1)
                .map(p => ({
                    provider: p,
                    places: ko.observable(null)
                }));
        } else {
            sponsor = defaultProviders
                .filter(p => blocked.indexOf(p) === -1)
                .map(p => ({
                    provider: p,
                    places: ko.observable(null)
                }));
        }

        this.getTemplateViewModel().provider_sponsor(sponsor);

        // VIN Duplicate Priority: reordered version of the master providers list
        const vinPriority = (settings && settings.vin_duplicate_priority && Array.isArray(settings.vin_duplicate_priority))
            ? settings.vin_duplicate_priority
            : providers.slice();
        this.getTemplateViewModel().vin_duplicate_priority(vinPriority);

        // DB Force: single provider, max 12 places
        const viewModel = this.getTemplateViewModel();
        if (settings && settings.db_force && typeof settings.db_force === 'object' && settings.db_force.provider) {
            viewModel.db_force_provider(settings.db_force.provider);
            const p = settings.db_force.places;
            const places = typeof p === 'number' && p >= 1 && p <= 12 ? p : 1;
            viewModel.db_force_places(places);
            const savedExtraField = settings.db_force.extraConditionField;
            const savedExtraValue = settings.db_force.extraConditionValue;
            viewModel.db_force_extraConditionField(
                savedExtraField != null && savedExtraField !== '' ? savedExtraField : null
            );
            viewModel.db_force_extraConditionValue(
                savedExtraValue != null && savedExtraValue !== '' ? String(savedExtraValue) : ''
            );
        } else {
            viewModel.db_force_provider(null);
            viewModel.db_force_places(1);
            viewModel.db_force_extraConditionField(null);
            viewModel.db_force_extraConditionValue('');
            viewModel.db_force_newProvider(null);
        }

        for (let i = 0; i < MP_HOME_TAG_SECTIONS.length; i++) {
            const section = MP_HOME_TAG_SECTIONS[i];
            let rows = normalizeTagWeekRows(settings && settings[section.settingsKey], section.max);
            if (rows.length === 0
                && section.filterId === MP_HOME_FILTER_FEATURED
                && settings
                && settings.home_page_listing
                && settings.home_page_listing.featured_tag_id != null) {
                rows = [{ id: settings.home_page_listing.featured_tag_id, name: "" }];
            }
            viewModel[section.vmKey](rows);
            const filter = this.homeTagFilters[section.filterId];
            if (filter) {
                filter.setSelectedTagsFromRequestParams(rows);
            }
        }

        this.initBestCategoryUi(settings);
        this.initBuyingGuidesUi(settings);
    }

    public getMarketplaceSettings() {
        const vm = this.getTemplateViewModel();
        const sponsor = vm.provider_sponsor().map(entry => {
            const placesVal = parseInt(ko.unwrap(entry.places), 10);
            const places = isNaN(placesVal) ? null : placesVal;
            return {
                provider: entry.provider,
                places: places
            };
        });

        const dbForceProvider = vm.db_force_provider();
        const dbForcePlaces = parseInt(ko.unwrap(vm.db_force_places), 10);
        const extraField = vm.db_force_extraConditionField() || null;
        const extraValue = (extraField && vm.db_force_extraConditionValue().trim() !== '')
            ? vm.db_force_extraConditionValue().trim()
            : null;
        const db_force = dbForceProvider
            ? {
                provider: dbForceProvider,
                places: (!isNaN(dbForcePlaces) && dbForcePlaces >= 1 && dbForcePlaces <= 12) ? dbForcePlaces : 1,
                extraConditionField: extraField,
                extraConditionValue: extraValue
            }
            : null;

        const settings: any = {
            providers: vm.providers(),
            provider_sponsor: sponsor,
            blocked_providers: vm.blocked_providers(),
            vin_duplicate_priority: vm.vin_duplicate_priority(),
            db_force,
            home_page_listing: {
                featured_tag_id: null
            }
        };
        if (vm.internalMode()) {
            settings.tier_sponsor = sponsor;
        }

        for (let i = 0; i < MP_HOME_TAG_SECTIONS.length; i++) {
            const section = MP_HOME_TAG_SECTIONS[i];
            const rows = vm[section.vmKey]();
            settings[section.settingsKey] = rows;
            if (section.filterId === MP_HOME_FILTER_FEATURED) {
                settings.home_page_listing.featured_tag_id = rows.length > 0 ? rows[0].id : null;
            }
        }

        settings[MP_BEST_CATEGORY_KEY] = [];
        for (let i = 0; i < MP_BEST_CATEGORY_COUNT; i++) {
            const saved = vm["marketplace_best_slot_" + i]();
            const rowMeta = this.bestCategoryRows[i] || { label: "", short_label: "" };
            settings[MP_BEST_CATEGORY_KEY].push({
                label: rowMeta.label,
                short_label: rowMeta.short_label,
                tag: saved.length ? saved[0] : null
            });
        }

        settings[MP_BUYING_GUIDES_KEY] = [];
        for (let i = 0; i < MP_BUYING_GUIDES_COUNT; i++) {
            settings[MP_BUYING_GUIDES_KEY].push(vm["marketplace_buying_guide_slot_" + i]());
        }

        return settings;
    }
}



// WEBPACK FOOTER //
// ./js/components/templates/config_marketplace_template.ts